import { addDaysUtc, daysBetweenUtc } from './schedule-dates';
import { isScheduleDate } from './script-scheduling';
import type { WorkloadRow, WorkloadScope } from './throughput-workload';
import { weekEndOf, weekStartOf } from './window';

export const OPERATIONS_TIME_ZONE = 'America/Los_Angeles';
export type OperationsPeriod = 'daily' | 'weekly';
export type OperationsView = OperationsPeriod | 'monthly';
export const ACTIVITY_LABELS = {
  footageApproved: 'Footage approved', footageSubmitted: 'Footage submitted',
  scriptsApproved: 'Scripts approved', scriptsSubmitted: 'Scripts submitted', gradingRuns: 'Grading runs',
} as const;
export type ActivityMetric = keyof typeof ACTIVITY_LABELS;
export interface OperationsActivity { id: string; row: WorkloadRow; metric: ActivityMetric; at: number; day: string }
export type ActionReason = 'due-today' | 'overdue' | 'revisions' | 'am-review' | 'brand-review' | 'blocked';
export const ACTION_LABELS: Record<ActionReason, string> = {
  'due-today': 'Due today', overdue: 'Not submitted — overdue', revisions: 'Revisions required',
  'am-review': 'Waiting for AM', 'brand-review': 'Waiting for brand', blocked: 'Blocked by script approval',
};
export interface OperationsAction {
  id: string; row: WorkloadRow; ladder: 'script' | 'footage'; reason: ActionReason;
  owner: 'creator' | 'am' | 'brand'; dueOn: string | null; daysOverdue: number;
}
export interface OperationsSetup { creatorEmail: string; creator: string; brandSlug: string; brand: string; reason: string }
export interface OperationsReading {
  scope: WorkloadScope; title: string; today: string; date: string; period: OperationsPeriod;
  from: string; to: string; activity: OperationsActivity[];
  totals: Record<ActivityMetric, number>; actions: OperationsAction[];
  setup: OperationsSetup[]; expected: WorkloadRow[]; archived: boolean; notes: string[];
  deliveryRows?: WorkloadRow[];
}

const reportingFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: OPERATIONS_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
});
/** Group server instants by the reporting timezone, never by the host's timezone. */
export function operationsDay(at = Date.now()): string {
  const parts = reportingFormatter.formatToParts(new Date(at));
  const get = (name: string) => parts.find((part) => part.type === name)!.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * The instant an operations day begins (ms): its midnight in the reporting
 * timezone. A clock change happens at 2am there, so midnight is always one of
 * the zone's two offsets; the one whose instant `operationsDay` reads back as
 * this day, with the millisecond before it the day before, is the answer.
 */
export function operationsDayStart(day: string): number {
  for (const offset of ['-07:00', '-08:00']) {
    const t = Date.parse(`${day}T00:00:00${offset}`);
    if (operationsDay(t) === day && operationsDay(t - 1) !== day) return t;
  }
  return Date.parse(`${day}T00:00:00-08:00`);
}

export function operationsRange(date: string | undefined, period: OperationsPeriod, today = operationsDay()) {
  const selected = isScheduleDate(date) ? date : today;
  return { date: selected, from: period === 'daily' ? selected : weekStartOf(selected), to: period === 'daily' ? selected : weekEndOf(weekStartOf(selected)) };
}

/** One first submission per deliverable; resubmissions are work, not extra output.
 * An undone approval is no longer completed output. Reapproval moves the one
 * completion to its new date. Daily and weekly are sums of this same ledger. */
export function operationsActivity(rows: WorkloadRow[], from: string, to: string): OperationsActivity[] {
  const out: OperationsActivity[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    if (!row.scriptId || seen.has(row.scriptId)) continue;
    seen.add(row.scriptId);
    const push = (metric: ActivityMetric, at: number, id: string) => {
      if (!Number.isSafeInteger(at) || at <= 0 || at > 8_640_000_000_000_000) return;
      const day = operationsDay(at);
      if (day >= from && day <= to) out.push({ id, row, metric, at, day });
    };
    const events = [...(row.activity ?? [])].sort((a, b) => a.at - b.at || a.eventId.localeCompare(b.eventId));
    for (const ladder of ['script', 'footage'] as const) {
      const first = events.find((event) => event.ladder === ladder && event.type === 'submitted');
      if (first) push(ladder === 'script' ? 'scriptsSubmitted' : 'footageSubmitted', first.at, first.eventId);
      const stage = ladder === 'script' ? row.scriptStage : row.footageStage;
      const approval = events.filter((event) => event.ladder === ladder && event.type === 'approved').at(-1);
      if (stage === 'approved' && approval) push(ladder === 'script' ? 'scriptsApproved' : 'footageApproved', approval.at, approval.eventId);
    }
    row.grades.forEach((run, index) => push('gradingRuns', run.gradedAt, `${row.scriptId}:grade:${index}`));
  }
  return out.sort((a, b) => b.at - a.at || a.id.localeCompare(b.id));
}

/** Current operations, even when the activity selector is looking at last week. */
export function operationsActions(rows: WorkloadRow[], today: string, activeBrands: ReadonlySet<string>): OperationsAction[] {
  const out: OperationsAction[] = [];
  for (const row of rows) {
    if (!activeBrands.has(row.brandSlug)) continue;
    for (const ladder of ['script', 'footage'] as const) {
      const stage = ladder === 'script' ? row.scriptStage : row.footageStage;
      if (stage === 'approved' || stage === 'unknown') continue;
      let dueOn = ladder === 'script' ? row.schedule?.currentScriptDueOn ?? row.scriptDueOn : row.schedule?.currentFootageDueOn ?? row.footageDueOn;
      let reason: ActionReason | undefined;
      let owner: OperationsAction['owner'] = 'creator';
      if (stage === 'awaiting-am-review' || stage === 'awaiting-brand-review') {
        owner = stage === 'awaiting-am-review' ? 'am' : 'brand';
        reason = owner === 'am' ? 'am-review' : 'brand-review';
        dueOn = row.milestones?.[`${ladder}-${owner}-review-due`] ?? null;
      } else if (stage === 'edits-requested') {
        reason = 'revisions';
      } else if (dueOn && dueOn <= today) {
        if (ladder === 'footage' && row.scriptStage !== 'approved') {
          reason = 'blocked';
          owner = row.scriptStage === 'awaiting-brand-review' ? 'brand' : row.scriptStage === 'awaiting-am-review' ? 'am' : 'creator';
        } else reason = dueOn < today ? 'overdue' : 'due-today';
      }
      if (reason) out.push({ id: `${row.key}:${ladder}`, row, ladder, reason, owner, dueOn,
        daysOverdue: dueOn && dueOn < today ? daysBetweenUtc(dueOn, today) : 0 });
    }
  }
  const priority: Record<ActionReason, number> = { overdue: 0, revisions: 1, 'due-today': 2, 'am-review': 3, 'brand-review': 4, blocked: 5 };
  return out.sort((a, b) => priority[a.reason] - priority[b.reason] || b.daysOverdue - a.daysOverdue || a.row.creator.localeCompare(b.row.creator) || a.id.localeCompare(b.id));
}

export const nextOperationsDate = (date: string, period: OperationsPeriod, direction: -1 | 1) => addDaysUtc(date, direction * (period === 'daily' ? 1 : 7));
